import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from '../../juristec-ui/core/Avatar';
import Toggle from '../../juristec-ui/core/Toggle';
import Tooltip from '../../juristec-ui/core/Tooltip';
import {
  Exit, Admin, Users, Moon,
} from '../../juristec-ui/icons';
import availableLanguages from '../../juristec-ui/utils/language/availableLanguages';
import registerNamespace from './locales';
import {
  MainContainer,
  ProfileContainer,
  ProfileInfo,
  Icon,
  Item,
  SeparatorLine,
  MultiButton,
  LanguageButton,
} from './styled/ProfileConfig.styled';

const namespace = registerNamespace('src/components/ProfileConfig/ProfileConfig.jsx');

/**
* A component that contains settings related to the profile
*/
const ProfileConfig = ({
  openProfileEdit,
  closeProfileConfig,
  user,
  nightMode = false,
  handleNightMode,
  handleLanguage,
  actualLanguage,
  handleLogout,
  tourContext = { tourOpen: false },
}) => {
  const { t } = useTranslation(namespace);
  const history = useHistory();

  return (
    <MainContainer>
      <ProfileContainer onClick={openProfileEdit}>
        <Avatar name={user.name} src={user.src || null} size="large" />
        <ProfileInfo>
          <h2>{user.name}</h2>
          <span>
            {t('editProfile')}
          </span>
        </ProfileInfo>
      </ProfileContainer>

      <SeparatorLine />
      <Item onClick={handleNightMode}>
        <Icon><Moon /></Icon>
        <h3 style={{ marginRight: '10px' }}>
          {t('nightMode')}
        </h3>
        <Toggle size="small" checked={nightMode} handleChange={handleNightMode} />
      </Item>
      {/* Apenas o end do DEV possuí esse token */}
      {!!process.env.REACT_APP_APPCHECK_DEBUG_TOKEN && (
        <>
          <SeparatorLine />
          <Item style={{ cursor: 'default' }}>
            <Icon><Moon /></Icon>
            <h3 style={{ marginRight: '10px' }}>
              {t('language')}
            </h3>
            <MultiButton>
              {availableLanguages.map((language) => (
                <Tooltip
                  key={language.value}
                  text={language.label}
                  direction="top"
                  atModal
                >
                  <LanguageButton
                    selected={language.value === actualLanguage}
                    onClick={() => handleLanguage(language.value)}
                  >
                    <img
                      src={`https://flagcdn.com/24x18/${language.flag}.webp`}
                      width="20px"
                      height="15px"
                      alt={language.label}
                    />
                  </LanguageButton>
                </Tooltip>
              ))}
            </MultiButton>
          </Item>
        </>
      )}

      <SeparatorLine />
      {(user.isAdmin || user.isSupport) && (
        <Item onClick={() => { history.push('/admin'); closeProfileConfig(); }}>
          <Icon><Admin /></Icon>
          {user.isSupport ? (
            <h3>{t('support')}</h3>
          ) : (
            <h3>{t('administration')}</h3>
          )}
        </Item>
      )}
      {!!user.role && user.role !== 'guest' && (
        <Item onClick={() => { history.push('/usuarios'); closeProfileConfig(); if (tourContext.tourOpen) tourContext.nextStep(); }} className="gerenciar_instacia">
          <Icon><Users /></Icon>
          <h3>{t('manageInstance')}</h3>
        </Item>
      )}
      <Item onClick={() => handleLogout()}>
        <Icon><Exit /></Icon>
        <h3>{t('exit')}</h3>
      </Item>
    </MainContainer>
  );
};

ProfileConfig.propTypes = {
  /**
  * A function to open the profile editing component
  */
  openProfileEdit: PropTypes.func.isRequired,
  /**
  * Function to close de ProfileConfig popover
  */
  closeProfileConfig: PropTypes.func.isRequired,
  /**
  * User profile data (only the name and the picture is used)
  */
  user: PropTypes.shape({
    name: PropTypes.string,
    src: PropTypes.string,
    isAdmin: PropTypes.bool,
    isSupport: PropTypes.bool,
    role: PropTypes.string,
  }).isRequired,
  /**
  * A boolean state that indicates whether is to use night mode theme or not
  */
  nightMode: PropTypes.bool,
  /**
  * A function to perform some action when the style state changes
  */
  handleNightMode: PropTypes.func.isRequired,
  /**
   * A function to perform some action when the language state changes
   */
  handleLanguage: PropTypes.func.isRequired,
  /**
   * Actual language selected
   */
  actualLanguage: PropTypes.string.isRequired,
  /**
  * A function to logout the current user
  */
  handleLogout: PropTypes.func.isRequired,
};

export default ProfileConfig;
